import { Controller } from "stimulus"

type AutoSaveValue = string | number | boolean | string[] | null
type AutoSaveField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

interface PersistOptions {
  // Interim saves fired by the poller stay quiet: the status line reports them,
  // a toast every couple of seconds would not.
  silent?: boolean
  // What the toast's Undo button restores. Defaults to the last saved value.
  undoValue?: AutoSaveValue
}

const DEFAULT_POLL_INTERVAL = 2000

export default class AutoSaveController extends Controller {
  static values = {
    url: String,
    field: String,
    poll: Boolean,
    interval: Number
  }

  static targets = ["status"]

  readonly urlValue!: string
  readonly fieldValue!: string
  readonly pollValue!: boolean
  readonly intervalValue!: number
  readonly statusTarget!: HTMLElement
  readonly hasStatusTarget!: boolean

  private pollTimer: number | null = null
  private inFlight = false
  private pendingCommit: AutoSaveField | null = null
  private lastMessage: string | null = null

  connect(): void {}

  disconnect(): void {
    this.stopPolling()
  }

  // Rich-text fields keep saving while the user is still typing. Summernote
  // dispatches focus/blur on the underlying textarea; between the two we re-read
  // the editor every `intervalValue` ms and persist whatever changed.
  startPolling(): void {
    if (!this.pollValue || this.pollTimer !== null) return

    this.pollTimer = window.setInterval(
      () => this.flush(),
      this.intervalValue || DEFAULT_POLL_INTERVAL
    )
  }

  stopPolling(): void {
    if (this.pollTimer === null) return

    window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  save(event: Event): void {
    this.stopPolling()
    this.commit(event.target as AutoSaveField)
  }

  private commit(element: AutoSaveField): void {
    if (this.inFlight) {
      // A poll save is still on the wire. Re-run once it settles rather than
      // racing it, so the newest text is the one that lands last.
      this.pendingCommit = element
      return
    }

    const previousValue = this.previousValue(element)
    const value = this.currentValue(element)

    if (this.valuesEqual(previousValue, value)) {
      // The poller already wrote this text. Still offer an undo back to whatever
      // the editor held when the user focused it.
      this.announceSettled(element, value)
      return
    }

    this.persist(element, value, previousValue, { undoValue: this.undoBaseline(element, previousValue) })
  }

  private flush(): void {
    const element = this.fieldElement
    if (!element || this.inFlight) return

    const previousValue = this.previousValue(element)
    const value = this.currentValue(element)

    if (this.valuesEqual(previousValue, value)) return

    this.persist(element, value, previousValue, { silent: true })
  }

  private persist(
    element: AutoSaveField,
    value: AutoSaveValue,
    previousValue: AutoSaveValue,
    { silent = false, undoValue }: PersistOptions = {}
  ): void {
    // Disabling stops a click-happy toggle from firing twice. A rich-text field
    // is still being typed into, so it has to stay enabled.
    const disable = !this.isRichText(element)
    if (disable) element.disabled = true

    this.inFlight = true
    this.setStatus("Saving…")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        field: this.fieldValue,
        value: value,
        assignment_id: element.dataset.assignmentId
      })
    })
      .then(response => {
        return response.json().then(data => {
          if (!response.ok) throw data
          return data
        })
      })
      .then(data => {
        if (data.success) {
          this.lastMessage = data.message || null
          this.storePreviousValue(element, value)
          this.setStatus("Saved")
          this.dispatchSaved(value)
          if (!silent) {
            this.dispatchToast(
              data.message || "Changes saved",
              false,
              undoValue === undefined ? previousValue : undoValue,
              this.undoParams(element)
            )
          }
        } else {
          console.error("Failed to save:", data.message)
          this.rollBack(element, previousValue)
          this.dispatchToast(data.message || "Failed to save changes", true)
        }
      })
      .catch(error => {
        console.error("Error saving field:", error)
        this.rollBack(element, previousValue)
        this.dispatchToast(error.message || "Failed to save changes", true)
      })
      .then(() => {
        this.inFlight = false
        if (disable) element.disabled = false
        this.refreshSelectpicker(element)

        const pending = this.pendingCommit
        this.pendingCommit = null
        if (pending) this.commit(pending)
      })
  }

  // A failed save must not yank text out from under someone mid-sentence, so a
  // rich-text field keeps what the user typed and simply stops polling; the next
  // focus (or the blur save) retries it. Everything else snaps back.
  private rollBack(element: AutoSaveField, previousValue: AutoSaveValue): void {
    this.setStatus("Not saved")

    if (this.isRichText(element)) {
      this.stopPolling()
      return
    }

    this.revertValue(element, previousValue)
  }

  private announceSettled(element: AutoSaveField, value: AutoSaveValue): void {
    const undoValue = element.dataset.undoValue
    if (undoValue === undefined || undoValue === String(value)) return

    delete element.dataset.undoValue
    this.dispatchToast(this.lastMessage || "Changes saved", false, undoValue, this.undoParams(element))
  }

  private undoBaseline(element: AutoSaveField, previousValue: AutoSaveValue): AutoSaveValue {
    const undoValue = element.dataset.undoValue
    if (undoValue === undefined) return previousValue

    delete element.dataset.undoValue
    return undoValue
  }

  private get fieldElement(): AutoSaveField | null {
    return this.element.querySelector<AutoSaveField>("[data-action*='auto-save#save']")
  }

  private isRichText(element: AutoSaveField): boolean {
    return element.tagName === "TEXTAREA" && !!$(element).data("summernote")
  }

  private currentValue(element: AutoSaveField): AutoSaveValue {
    if (this.isRichText(element)) {
      // Reads the code view's source when it is open, the editable body otherwise.
      return ($(element) as any).summernote("code") as string
    } else if (element.type === "checkbox") {
      return (element as HTMLInputElement).checked
    } else if (element.type === "radio") {
      return (element as HTMLInputElement).value
    } else if (element.tagName === "SELECT" && (element as HTMLSelectElement).multiple) {
      return Array.from((element as HTMLSelectElement).selectedOptions).map(option => option.value)
    } else {
      return element.value
    }
  }

  private previousValue(element: AutoSaveField): AutoSaveValue {
    if (element.type === "checkbox") {
      return !(element as HTMLInputElement).checked
    } else if (element.type === "radio") {
      const radioGroup = document.querySelectorAll(`input[name="${element.getAttribute("name")}"]`)
      let previous = ""
      radioGroup.forEach((radio: HTMLInputElement) => {
        if (radio !== element && radio.dataset.previouslyChecked === "true") previous = radio.value
      })
      return previous
    } else if (element.tagName === "SELECT" && (element as HTMLSelectElement).multiple) {
      return element.dataset.previousValue ? JSON.parse(element.dataset.previousValue) : []
    } else {
      return element.dataset.previousValue || ""
    }
  }

  private storePreviousValue(element: AutoSaveField, value: AutoSaveValue): void {
    if (element.type === "radio") {
      const radioGroup = document.querySelectorAll(`input[name="${element.getAttribute("name")}"]`)
      radioGroup.forEach((radio: HTMLInputElement) => {
        radio.dataset.previouslyChecked = (radio === element).toString()
      })
    } else if (Array.isArray(value)) {
      element.dataset.previousValue = JSON.stringify(value)
    } else if (value !== null) {
      element.dataset.previousValue = String(value)
      // Keep the textarea behind summernote in step, so anything reading the raw
      // field (previews, test emails) sees what was actually persisted.
      if (this.isRichText(element)) element.value = String(value)
    }
  }

  private revertValue(element: AutoSaveField, previousValue: AutoSaveValue): void {
    if (element.type === "checkbox") {
      (element as HTMLInputElement).checked = previousValue === true
    } else if (element.type === "radio") {
      const radioGroup = document.querySelectorAll(`input[name="${element.getAttribute("name")}"]`)
      radioGroup.forEach((radio: HTMLInputElement) => {
        if (radio.value === previousValue) {
          radio.checked = true
          radio.dataset.previouslyChecked = "true"
        } else {
          radio.dataset.previouslyChecked = "false"
        }
      })
    } else if (element.tagName === "SELECT" && (element as HTMLSelectElement).multiple) {
      const selectElement = element as HTMLSelectElement
      const values = Array.isArray(previousValue) ? previousValue : []
      Array.from(selectElement.options).forEach(option => {
        option.selected = values.includes(option.value)
      })
      selectElement.dataset.previousValue = JSON.stringify(values)
    } else {
      element.value = previousValue === null ? "" : String(previousValue)
      element.dataset.previousValue = element.value
    }
    this.refreshSelectpicker(element)
  }

  private valuesEqual(previousValue: AutoSaveValue, value: AutoSaveValue): boolean {
    return JSON.stringify(previousValue) === JSON.stringify(value)
  }

  private setStatus(message: string): void {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
  }

  private undoParams(element: AutoSaveField): Record<string, string> {
    const params: Record<string, string> = {}
    if (element.dataset.assignmentId) params.assignment_id = element.dataset.assignmentId
    return params
  }

  // Lets the surrounding page react to a persisted field (e.g. re-enabling the
  // "Send me a test email" link that a pending edit had greyed out).
  private dispatchSaved(value: AutoSaveValue): void {
    this.element.dispatchEvent(
      new CustomEvent("auto-save:saved", {
        detail: { field: this.fieldValue, value },
        bubbles: true
      })
    )
  }

  private dispatchToast(message: string, error: boolean, undoValue?: AutoSaveValue, undoParams?: Record<string, string>): void {
    const toastEvent = new CustomEvent("show-toast", {
      detail: {
        message,
        error,
        undoUrl: error ? undefined : this.urlValue,
        undoField: error ? undefined : this.fieldValue,
        undoValue,
        undoParams: error ? undefined : undoParams
      },
      bubbles: true
    })

    this.element.dispatchEvent(toastEvent)
  }

  private refreshSelectpicker(element: AutoSaveField): void {
    if (element.tagName === "SELECT" && element.classList.contains("selectpickerr")) {
      ($(element) as any).selectpicker("refresh")
    }
  }
}
